import { useEffect, useState, SyntheticEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import FormHelperText from '@mui/material/FormHelperText';

// third-party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project-imports
import useAuth from '@/hooks/useAuth';
import useScriptRef from '@/hooks/useScriptRef';
import IconButton from '@/components/base-component/extended/IconButton';
import AnimateButton from '@/components/base-component/extended/AnimateButton';

import { strengthColor, strengthColorThai, strengthIndicator } from '@/utils/PasswordStrength';
import { openSnackbar } from '@/api/snackbar';

// types
import { SnackbarProps } from '@/types/bs-types/snackbar';
import { StringColorProps } from '@/types/bs-types/password';

// assets
import { Check, Eye, EyeSlash } from 'iconsax-react';
import BsForm from '@/components/bs-component/form/BsForm';
import { Controller, useForm } from 'react-hook-form';
import useApiUpdate from '@/hooks/useApiUpdate';
import { CardContent, Modal, Snackbar } from '@mui/material';
import { useIntl } from 'react-intl';
import MainCard from '@/components/base-component/MainCard';
import Cross from '@/assets/svgs/cross';

// ============================|| FIREBASE - RESET PASSWORD ||============================ //

export default function Setting({ open, onClose }: { open: boolean; onClose: () => void }) {
  const scriptedRef = useScriptRef();
  const { formatMessage } = useIntl();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      oldPassword: '',
      password: '',
      confirmPassword: ''
    }
  });
  const { handleUpdate, error: errorUpdated } = useApiUpdate(`/auth/${user?.id}`);
  const [level, setLevel] = useState<StringColorProps>();
  const [showPassword, setShowPassword] = useState(false);
  const [showOldPassword, setOldShowPassword] = useState(false);
  const [isError, setIsError] = useState<boolean>(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowOldPassword = () => {
    setOldShowPassword(!showOldPassword);
  };

  const handleMouseDownPassword = (event: SyntheticEvent) => {
    event.preventDefault();
  };

  const changePassword = (value: string) => {
    const temp = strengthIndicator(value);
    setLevel(strengthColorThai(temp));
  };

  useEffect(() => {
    changePassword('');
  }, []);

  const onSubmit = async (values: any) => {
    const isEmptyValue = Object.entries(values).map(([key, value]) => {
      const excludeKeys = [''];
      return { key, value: ((value !== undefined || value !== null) && value !== '') || excludeKeys.includes(key) ? false : true };
    });

    const hasEmptyValue = isEmptyValue.some((entry) => entry.value === true);

    if (hasEmptyValue) {
      setIsError(hasEmptyValue);
      return;
    }

    // first condition check pass && confirm pass
    if (values.confirmPassword === values.password) {
      const res = await handleUpdate({ password: values.password, oldPassword: values.oldPassword });
      if (res.error) {
        openSnackbar({
          open: true,
          message: `${formatMessage({ id: 'snackbar.update.error' })}`,
          variant: 'alert',
          alert: {
            color: 'error'
          }
        } as SnackbarProps);
      } else {
        openSnackbar({
          open: true,
          message: `${formatMessage({ id: 'snackbar.update.success' })}`,
          variant: 'alert',
          alert: {
            color: 'success'
          }
        } as SnackbarProps);
        onClose();
      }
    } else {
      console.log('password not correct');
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <MainCard
        sx={{ width: 400 }}
        title={
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Stack spacing={1} direction={'row'}>
              <Check />
              <Typography variant="h5">ตั้งค่ารหัสผ่านใหม่</Typography>
            </Stack>
            <Stack sx={{ cursor: 'pointer' }}>
              <Cross onClick={onClose} />
            </Stack>
          </Stack>
        }
        modal
        darkTitle
        content={false}
      >
        <CardContent>
          <BsForm onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="old-password">รหัสผ่าน</InputLabel>
                  <Controller
                    name="oldPassword"
                    control={control}
                    render={({ field }) => (
                      <OutlinedInput
                        fullWidth
                        id="old-password"
                        type={showOldPassword ? 'text' : 'password'}
                        name="oldPassword"
                        onChange={field.onChange}
                        placeholder="รหัสผ่านของคุณ"
                        error={isError && (field.value === '' || field.value === undefined)}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowOldPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                              color="neutral"
                            >
                              {showOldPassword ? <Eye /> : <EyeSlash />}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="password-reset">รหัสผ่านใหม่</InputLabel>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <OutlinedInput
                        fullWidth
                        id="password-reset"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        error={isError && (field.value === '' || field.value === undefined)}
                        onChange={(e) => {
                          changePassword(e.target.value);
                          field.onChange(e.target.value);
                        }}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                              color="neutral"
                            >
                              {showPassword ? <Eye /> : <EyeSlash />}
                            </IconButton>
                          </InputAdornment>
                        }
                        placeholder="รหัสผ่านของคุณ"
                      />
                    )}
                  />
                </Stack>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Box sx={{ bgcolor: level?.color, width: 85, height: 8, borderRadius: '7px' }} />
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle1" fontSize="0.75rem">
                        {level?.label}
                      </Typography>
                    </Grid>
                  </Grid>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="confirm-password-reset">ยืนยัน รหัสผ่านใหม่</InputLabel>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <OutlinedInput
                        fullWidth
                        id="confirm-password-reset"
                        type="password"
                        error={isError && (field.value === '' || field.value === undefined)}
                        name="confirmPassword"
                        onChange={field.onChange}
                        placeholder="ยืนยัน รหัสผ่านของคุณ"
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <AnimateButton>
                  <Button disableElevation fullWidth size="large" type="submit" variant="contained" color="primary">
                    Reset Password
                  </Button>
                </AnimateButton>
              </Grid>
            </Grid>
          </BsForm>
        </CardContent>
      </MainCard>
    </Modal>
  );
}
